import { useState, useEffect } from "react";
import axios from "axios";
import Api from "../Api/Api.json";
import {
  ChevronDown,
  Menu,
  Play,
  Send,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Users,
  Globe,
  Hash,
  FileText,
  HelpCircle,
  Loader2,
} from "lucide-react";
import MainContainer from "../components/Containers/MainContainer";
import ContentContainer from "../components/Containers/ContentContainer";
import CardContainer from "../components/Containers/CardContainer";

const MathPage = () => {
  // Form state for dropdowns
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedMedium, setSelectedMedium] = useState(null);

  // Existing state variables
  const [tableValues, setTableValues] = useState(Array(5).fill(""));
  const [graphPoints, setGraphPoints] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [userResponse, setUserResponse] = useState("");
  const [analysisReport, setAnalysisReport] = useState("");
  const [analysisAudioLink, setAnalysisAudioLink] = useState("");
  const [dialogState, setDialogState] = useState("Waiting for your response");
  const [subjectProblemList, setSubjectProblemList] = useState({});
  const [chapterKeys, setChapterKeys] = useState([]);
  const [chapter, setChapter] = useState("1");
  const [section, setSection] = useState(1);
  const [problemNo, setProblemNo] = useState(1);
  const [problems, setProblems] = useState({});
  const [user_id, setUserID] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Dropdown options
  const levelOptions = [
    { value: "6", label: "ষষ্ঠ শ্রেণী" },
    { value: "7", label: "সপ্তম শ্রেণী" },
    { value: "8", label: "অষ্টম শ্রেণী" },
    { value: "9", label: "নবম শ্রেণী" },
    { value: "10", label: "দশম শ্রেণী" },
  ];

  const subjectOptions = [
    { value: "math", label: "গণিত" },
    { value: "physics", label: "পদার্থবিজ্ঞান" },
    { value: "chemistry", label: "রসায়ন" },
    { value: "biology", label: "জীববিজ্ঞান" },
    { value: "bangla", label: "বাংলা" },
    { value: "english", label: "ইংরেজি" },
  ];

  const mediumOptions = [
    { value: "bengali", label: "বাংলা মাধ্যম" },
    { value: "english", label: "ইংরেজি মাধ্যম" },
  ];

  // Generate dropdown options for chapter, section, and problem
  const getChapterOptions = () => {
    return chapterKeys.map((key) => ({
      value: key,
      label: `অধ্যায় ${key}`,
    }));
  };

  const getSectionOptions = () => {
    if (!subjectProblemList[chapter]) return [];
    return Array.from(
      { length: subjectProblemList[chapter].length },
      (_, i) => ({
        value: i + 1,
        label: `পরিচ্ছেদ ${i + 1}`,
      })
    );
  };

  const getProblemOptions = () => {
    if (
      !subjectProblemList[chapter] ||
      !subjectProblemList[chapter][section - 1]
    )
      return [];
    return Array.from(
      { length: subjectProblemList[chapter][section - 1] },
      (_, i) => ({
        value: i + 1,
        label: `সমস্যা ${i + 1}`,
      })
    );
  };

  const handleUserResponseChange = (e) => {
    setUserResponse(e.target.value);
  };

  const handleTableValueChange = (e, index) => {
    const newTableValues = [...tableValues];
    newTableValues[index] = e.target.value;
    setTableValues(newTableValues);
  };

  const handleGraphPointClick = (x, y) => {
    const newGraphPoints = [...graphPoints, { x, y }];
    setGraphPoints(newGraphPoints);
  };

  const handleFormSubmit = () => {
    if (selectedLevel && selectedSubject && selectedMedium) {
      loadProblemList(selectedLevel, selectedSubject, selectedMedium);
    }
  };

  // Handle chapter selection and reset dependent dropdowns
  const handleChapterChange = (newChapter) => {
    setChapter(newChapter);
    setSection(1); // Reset to first section
    setProblemNo(1); // Reset to first problem
  };

  // Handle section selection and reset problem dropdown
  const handleSectionChange = (newSection) => {
    setSection(newSection);
    setProblemNo(1); // Reset to first problem
  };

  useEffect(() => {
    if (localStorage.getItem("upskill_user_info") !== null) {
      const userid = JSON.parse(localStorage.getItem("upskill_user_info"));
      console.log(userid);
      setUserID(userid);
    }

    if (selectedLevel && selectedSubject && selectedMedium) {
      loadProblemList(selectedLevel, selectedSubject, selectedMedium);
    }
  }, []);

  useEffect(() => {
    if (!selectedLevel || !selectedSubject || !selectedMedium) return;

    setUserResponse("");
    setAnalysisReport("");
    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.SPOKEN_ENGLISH_USER_API;
    const requestData = {
      task: "getProblems",
      user_id: user_id,
      level: selectedLevel,
      subject: selectedSubject,
      medium: selectedMedium,
      chapter: chapter,
      section: section,
      problem_no: problemNo,
    };
    axios
      .post(url, requestData, headers)
      .then((response) => {
        setProblems(response.data);
      })
      .catch((error) => {
        console.error("Error loading problems:", error);
      });
    setCurrentIndex(0);
  }, [
    chapter,
    section,
    problemNo,
    selectedLevel,
    selectedSubject,
    selectedMedium,
  ]);

  const loadProblemList = async (level, subject, medium) => {
    setUserResponse("");
    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.SPOKEN_ENGLISH_USER_API;
    const requestData = {
      task: "getSubjectProblemList",
      level: level,
      subject: subject,
      medium: medium,
    };
    axios
      .post(url, requestData, headers)
      .then((response) => {
        setSubjectProblemList(response.data);
        const keys = Object.keys(response.data);
        setChapterKeys(keys);
        // Reset selections when new data is loaded
        if (keys.length > 0) {
          setChapter(keys[0]);
          setSection(1);
          setProblemNo(1);
        }
      })
      .catch((error) => {
        console.error("Error loading problem list:", error);
      });
  };

  const handleNext = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex + 1) % Object.keys(problems).length
    );
    setUserResponse("");
    setAnalysisReport("");
    setAnalysisAudioLink("");
  };

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? Object.keys(problems).length - 1 : prevIndex - 1
    );
    setUserResponse("");
    setAnalysisReport("");
    setAnalysisAudioLink("");
  };

  const analyzeAnswer = () => {
    if (userResponse === "") {
      setAnalysisReport("First, give your response");
      setDialogState("Waiting for your response");
      const audioPath = new Audio(
        `${process.env.PUBLIC_URL}/audio/no_answer.mp3`
      );
      audioPath.play();
      return;
    }
    
    setIsAnalyzing(true);
    setDialogState("Analyzing your response");
    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.SPOKEN_ENGLISH_USER_API;
    const requestData = {
      task: "analyzeSchoolAnswer",
      problem: problems[`${currentIndex + 1}`]?.problem,
      solution: problems[`${currentIndex + 1}`]?.solution,
      level: selectedLevel,
      subject: selectedSubject,
      medium: selectedMedium,
      chapter: chapter,
      section: section,
      problem_no: problemNo,
      sub_problem_no: problems[`${currentIndex + 1}`]?.subproblem_no,
      concept: problems[`${currentIndex + 1}`]?.concept,
      user_id: user_id,
      user_response: userResponse,
    };
    axios
      .post(url, requestData, headers)
      .then((response) => {
        if (response.data.length > 0) {
          setAnalysisReport(response.data[0]);
          setAnalysisAudioLink(response.data[1]);
          setDialogState("Analyzed your response");
        }
      })
      .catch((error) => {
        console.error("Error analyzing answer:", error);
      })
      .finally(() => {
        setIsAnalyzing(false);
      });
  };

  const playFeedbackAudio = () => {
    const audio = new Audio(analysisAudioLink);
    audio.play();
  };

  console.log(subjectProblemList);
  console.log(Object.keys(subjectProblemList).length);

  const CustomDropdown = ({
    options,
    value,
    onChange,
    placeholder,
    icon: Icon,
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 focus:border-blue-500 focus:outline-none transition-colors duration-200"
        >
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-gray-500" />
            <span className={value ? "text-gray-900" : "text-gray-500"}>
              {value
                ? options.find((opt) => opt.value === value)?.label
                : placeholder}
            </span>
          </div>
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-3 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <MainContainer>
      <ContentContainer>
        {/* Selection Form */}
        <CardContainer className="p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            আপনার পছন্দ নির্বাচন করুন
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                শ্রেণী
              </label>
              <CustomDropdown
                options={levelOptions}
                value={selectedLevel}
                onChange={setSelectedLevel}
                placeholder="শ্রেণী নির্বাচন করুন"
                icon={Users}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                বিষয়
              </label>
              <CustomDropdown
                options={subjectOptions}
                value={selectedSubject}
                onChange={setSelectedSubject}
                placeholder="বিষয় নির্বাচন করুন"
                icon={BookOpen}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                মাধ্যম
              </label>
              <CustomDropdown
                options={mediumOptions}
                value={selectedMedium}
                onChange={setSelectedMedium}
                placeholder="মাধ্যম নির্বাচন করুন"
                icon={Globe}
              />
            </div>
          </div>

          <button
            onClick={handleFormSubmit}
            disabled={!selectedLevel || !selectedSubject || !selectedMedium}
            className="w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
          >
            শুরু করুন
          </button>
        </CardContainer>

        {/* Navigation Dropdowns */}
        {Object.keys(subjectProblemList).length > 0 && (
          <CardContainer className="p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              নির্বাচন করুন
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  অধ্যায়
                </label>
                <CustomDropdown
                  options={getChapterOptions()}
                  value={chapter}
                  onChange={handleChapterChange}
                  placeholder="অধ্যায় নির্বাচন করুন"
                  icon={Hash}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  পরিচ্ছেদ
                </label>
                <CustomDropdown
                  options={getSectionOptions()}
                  value={section}
                  onChange={handleSectionChange}
                  placeholder="পরিচ্ছেদ নির্বাচন করুন"
                  icon={FileText}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  সমস্যা
                </label>
                <CustomDropdown
                  options={getProblemOptions()}
                  value={problemNo}
                  onChange={setProblemNo}
                  placeholder="সমস্যা নির্বাচন করুন"
                  icon={HelpCircle}
                />
              </div>
            </div>
          </CardContainer>
        )}

        {/* Problem Section */}
        {problems && Object.keys(problems).length > 0 && (
          <CardContainer className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <button
                  onClick={handlePrev}
                  className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="text-sm text-gray-600">
                  সমস্যা: {currentIndex + 1}
                </div>
                <button
                  onClick={handleNext}
                  className="p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-lg font-medium text-gray-900">
                  প্রশ্ন: {problems[`${currentIndex + 1}`]?.problem}
                </p>
              </div>

              <div>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={userResponse}
                    onChange={handleUserResponseChange}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                    placeholder="এখানে আপনার উত্তর লিখুন..."
                  />
                  <button
                    onClick={analyzeAnswer}
                    disabled={isAnalyzing}
                    className={`flex items-center gap-2 px-6 py-3 font-medium rounded-lg transition-colors duration-200 ${
                      isAnalyzing 
                        ? "bg-gray-400 cursor-not-allowed" 
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isAnalyzing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                    {isAnalyzing ? "" : "জমা দিন"}
                  </button>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4">
                <p className="text-lg font-medium text-gray-900">
                  {analysisReport || "আপনার উত্তরের জন্য অপেক্ষা করছি..."}
                </p>
                {analysisAudioLink && (
                  <button
                    onClick={playFeedbackAudio}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                  >
                    <Play className="w-4 h-4" />
                    ফিডব্যাক শুনুন
                  </button>
                )}
              </div>
            </div>
          </CardContainer>
        )}
      </ContentContainer>
    </MainContainer>
  );
};

export default MathPage;
